import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';

export default function QuotesBuilder({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const [existingWhoValues, setExistingWhoValues] = useState([]);
  const [showWhoDropdown, setShowWhoDropdown] = useState(false);
  
  // Form state
  const [quote, setQuote] = useState('');
  const [who, setWho] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetchUser(userId);
    fetchExistingWhoValues();
  }, []);

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchExistingWhoValues = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/quotes/who`);
      if (response.ok) {
        const data = await response.json();
        setExistingWhoValues(data);
      }
    } catch (error) {
      console.error('Error fetching existing who values:', error);
    }
  };

  const handleReset = () => {
    setQuote('');
    setWho('');
    setShowWhoDropdown(false);
    setSaveStatus(null);
    setErrorMessage('');
  };

  const handleWhoSelect = (selectedWho) => {
    setWho(selectedWho);
    setShowWhoDropdown(false);
  };

  const handleSave = async () => {
    // Validation
    if (!quote.trim()) {
      setSaveStatus('error');
      setErrorMessage('Quote is required');
      return;
    }

    try {
      // Get the highest order_number and add 1
      const quotesResponse = await fetch(`${getApiUrl()}/api/quotes`);
      let orderNumber = 1;
      if (quotesResponse.ok) {
        const quotes = await quotesResponse.json();
        if (quotes.length > 0) {
          const maxOrder = Math.max(...quotes.map(q => q.order_number || 0));
          orderNumber = maxOrder + 1;
        }
      }

      const response = await fetch(`${getApiUrl()}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quote: quote.trim(),
          who: who.trim() || null,
          order_number: orderNumber,
        }),
      });

      if (response.ok) {
        setSaveStatus('success');
        setErrorMessage('');
        // Refresh who values in case a new one was added
        fetchExistingWhoValues();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        setSaveStatus('error');
        setErrorMessage(errorData.error || 'Failed to create quote');
      }
    } catch (error) {
      console.error('Error creating quote:', error);
      setSaveStatus('error');
      setErrorMessage('Failed to create quote. Please try again.');
    }
  };

  const styles = getStyles(theme);

  return (
    <View style={styles.container}>
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Quotes Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        {/* Title Section */}
        <View style={styles.titleSection}>
          <Text style={styles.title}>Quotes Builder</Text>
          <Text style={styles.subtitle}>
            Place to enter Quotes from Ming Ch'uan. Lets face it, they're mostly from Sibo. But all are welcome.
          </Text>
        </View>

        {/* Success/Error Status Box */}
        {saveStatus && (
          <View style={styles.form}>
            <View style={[
              styles.statusBox,
              saveStatus === 'success' ? styles.successBox : styles.errorBox
            ]}>
              <Text style={[
                styles.statusBoxTitle,
                saveStatus === 'success' && styles.successBoxText,
                saveStatus === 'error' && styles.errorBoxText
              ]}>
                {saveStatus === 'success' ? 'Record Created Successfully!' : 'Error'}
              </Text>
              {saveStatus === 'error' && errorMessage && (
                <Text style={styles.errorMessage}>{errorMessage}</Text>
              )}
              {saveStatus === 'success' && (
                <View style={styles.statusButtons}>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.backButton]}
                    onPress={() => navigation.navigate('BuilderMain', { userId: currentUser?.id || 1 })}
                  >
                    <Text style={styles.statusButtonText}>Back to Builders page</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.resetStatusButton]}
                    onPress={handleReset}
                  >
                    <Text style={styles.statusButtonText}>Reset form</Text>
                  </TouchableOpacity>
                </View>
              )}
              {saveStatus === 'error' && (
                <TouchableOpacity
                  style={[styles.statusButton, styles.resetStatusButton]}
                  onPress={() => {
                    setSaveStatus(null);
                    setErrorMessage('');
                  }}
                >
                  <Text style={styles.statusButtonText}>Try Again</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {/* Form Fields */}
        {!saveStatus && (
        <View style={styles.form}>
          {/* Quote */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Quote *</Text>
            <Text style={styles.helpText}>
              Enter the full quote here
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={quote}
              onChangeText={setQuote}
              placeholder="Enter the full quote"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
            />
          </View>

          {/* Who - Soft Dropdown */}
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Who</Text>
            <View style={styles.comboboxContainer}>
              <TextInput
                style={styles.comboboxInput}
                value={who}
                onChangeText={setWho}
                onFocus={() => setShowWhoDropdown(true)}
                placeholder="Select existing or enter new name"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              />
              <TouchableOpacity
                style={styles.dropdownToggle}
                onPress={() => setShowWhoDropdown(!showWhoDropdown)}
              >
                <Text style={styles.dropdownToggleText}>
                  {showWhoDropdown ? '▲' : '▼'}
                </Text>
              </TouchableOpacity>
            </View>
            
            {/* Dropdown List */}
            {showWhoDropdown && existingWhoValues.length > 0 && (
              <View style={styles.dropdownList}>
                {existingWhoValues.map((whoValue, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.dropdownListItem}
                    onPress={() => handleWhoSelect(whoValue)}
                  >
                    <Text style={styles.dropdownListItemText}>{whoValue}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.resetButton]}
              onPress={handleReset}
            >
              <Text style={styles.buttonText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.saveButton]}
              onPress={handleSave}
            >
              <Text style={styles.buttonText}>Save</Text>
            </TouchableOpacity>
          </View>
        </View>
        )}
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: 'transparent',
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  titleSection: {
    marginBottom: 30,
    alignItems: 'center',
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: theme.isDarkMode ? '#FDFD0A' : '#000000',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: 1,
    ...(Platform.OS === 'web' ? {
      fontFamily: 'serif, Georgia, "Times New Roman", Times, serif',
      textTransform: 'uppercase',
    } : {
      textTransform: 'uppercase',
    }),
  },
  subtitle: {
    fontSize: 14,
    fontStyle: 'italic',
    color: theme.colors.text,
    textAlign: 'center',
    paddingHorizontal: 20,
    lineHeight: 20,
    opacity: 0.8,
  },
  form: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  helpText: {
    fontSize: 12,
    color: theme.colors.text,
    opacity: 0.7,
    marginBottom: 8,
    fontStyle: 'italic',
  },
  input: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  textArea: {
    minHeight: 150,
    textAlignVertical: 'top',
  },
  comboboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  comboboxInput: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginRight: 8,
  },
  dropdownToggle: {
    width: 40,
    height: 40,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropdownToggleText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  dropdownList: {
    marginTop: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    maxHeight: 200,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    } : {
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  dropdownListItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  dropdownListItemText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
    gap: 15,
  },
  button: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  resetButton: {
    backgroundColor: '#999',
  },
  saveButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  // Status Box Styles
  statusBox: {
    width: '100%',
    padding: 30,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    } : {
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    }),
  },
  successBox: {
    backgroundColor: '#d4af37', // Yellow
  },
  errorBox: {
    backgroundColor: '#DC3545', // Red
  },
  statusBoxTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  successBoxText: {
    color: '#000000', // Black text on yellow background
  },
  errorBoxText: {
    color: '#FFFFFF', // White text on red background
  },
  errorMessage: {
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 20,
    paddingHorizontal: 10,
  },
  statusButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 20,
    gap: 15,
  },
  statusButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: '#000000',
  },
  resetStatusButton: {
    backgroundColor: '#333333',
  },
  statusButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});
